import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, MapPin, Mail, ArrowUpRight } from "lucide-react";
import { useI18n } from "../i18n";
import { CmsImage } from "../lib/images";
import { OFFICES } from "./ContactGlobal";

/**
 * Footer premium — mismo lenguaje visual del site (chrome / cyan / glass).
 * Estructura:
 *   1. CTA superior — franja con marco cromado invitando a WhatsApp
 *   2. Grid con logo, servicios, sucursales y contacto
 *   3. Barra inferior con copyright + badges de confianza
 */
export default function Footer({ content }) {
  const { t, pick } = useI18n();
  const BRANCHES = ["mx", "co", "br", "ar"].map((id) => OFFICES.find((o) => o.id === id));

  return (
    <footer className="relative overflow-hidden pt-2 pb-24 sm:pb-5" data-testid="footer">
      {/* Top glow separator */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-px bg-cyan-300 shadow-[0_0_18px_rgba(0,240,255,0.7)]" />

      {/* Ambient background */}
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-cyan-500/10 blur-[160px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-emerald-400/8 blur-[160px] pointer-events-none" />
      <div className="absolute inset-0 grid-bg opacity-[0.05] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-5 sm:px-6 md:px-10">
        {/* ═══════ MAIN GRID ═══════ */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 items-center justify-items-center text-center">
          {/* Brand block */}
          <div className="flex flex-col items-center">
            <CmsImage slotKey="brand.logo" defaultSrc="/xega-logo-new.png" alt="XEGA"
              className="h-12 w-auto drop-shadow-[0_0_14px_rgba(0,240,255,0.45)]"
              style={{ imageRendering: '-webkit-optimize-contrast' }} />
            <p className="mt-4 label-mono !text-white/85 !tracking-[0.32em] text-[11px]">
              {pick("ALTA TECNOLOGÍA EN SEGURIDAD", "HIGH-TECH SECURITY", "ALTA TECNOLOGIA EM SEGURANÇA")}
            </p>
          </div>

          {/* Branches */}
          <div className="flex flex-col items-center">
            <ul className="space-y-2.5">
              {BRANCHES.map((b) => (
                <li key={b.id} className="flex items-center gap-2 text-sm text-white/75" data-testid={`footer-branch-${b.id}`}>
                  <span aria-hidden="true" className="text-base leading-none">{b.flag}</span>
                  <span className="w-[74px] text-left">{pick(b.country_es, b.country_en, b.country_pt)}</span>
                  <a href={`tel:${b.phone.replace(/\s/g, "")}`} className="font-mono-x text-[12.5px] text-cyan-200/90 hover:text-cyan-100 transition"
                    data-testid={`footer-phone-${b.id}`}>
                    {b.phone}
                  </a>
                </li>
              ))}
            </ul>
            <Link
              to="/contacto"
              className="mt-4 inline-flex items-center gap-1 label-mono !text-cyan-300/90 hover:!text-cyan-200 !tracking-[0.28em] text-[9.5px] transition"
              data-testid="footer-view-all-branches"
            >
              <MapPin className="w-3 h-3" />
              {pick("VER TODAS", "VIEW ALL", "VER TODAS")}
              <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>

          {/* Contact */}
          <div className="flex flex-col items-center">
            <div className="label-mono mb-4 !text-cyan-300/90 !tracking-[0.32em] text-[10px]">
              {pick("CONTACTO 24/7", "CONTACT 24/7", "CONTATO 24/7")}
            </div>
            <ul className="space-y-3">
              <li className="flex items-center gap-2.5 text-sm text-white/80">
                <span className="w-7 h-7 rounded-md border border-cyan-400/30 bg-cyan-400/10 flex items-center justify-center">
                  <Mail className="w-3.5 h-3.5 text-cyan-300" />
                </span>
                <a href="mailto:info@xega.com.mx" className="hover:text-cyan-200 transition font-mono-x text-[13px]">
                  info@xega.com.mx
                </a>
              </li>
              <li className="flex items-center gap-2.5 text-sm text-white/80">
                <span className="w-7 h-7 rounded-md border border-emerald-400/30 bg-emerald-400/10 flex items-center justify-center">
                  <MessageCircle className="w-3.5 h-3.5 text-emerald-300" />
                </span>
                <Link to="/contacto" className="hover:text-emerald-200 transition text-[13px] font-tech">
                  {pick("Elige tu país", "Pick your country", "Escolha seu país")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom divider */}
        <div className="mt-8 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

        {/* Bottom row */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-center">
          <div className="label-mono !text-white/55 text-[10px]">
            © {new Date().getFullYear()} XEGA. {t("footer.rights")}
          </div>
          <span className="hidden sm:inline opacity-30 label-mono !text-white/40 text-[10px]">·</span>
          <div className="flex items-center gap-4 label-mono !text-white/40 text-[10px]">
            <a href="#" className="hover:!text-cyan-300 transition">{t("footer.privacy")}</a>
            <span className="opacity-30">·</span>
            <div className="flex items-center gap-1.5">
              <span className="pulse-dot" />
              <span>SYSTEM ONLINE · v1.0</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
